import { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Clock, ChevronUp, ChevronDown } from "lucide-react";

/**
 * Time slot picker (15-minute grid) — Booking.com style.
 * - Diurno (08:00-20:45)  → sfondo bianco
 * - Notturno (21:00-07:45) → sfondo azzurro chiaro
 * - Slot selezionato → arancione
 *
 * Props:
 *   value: "HH:MM" | ""
 *   onChange(time)
 *   placeholder, dataTestid, intervalMin (default 15)
 */
export default function TimeSlotPicker({ value, onChange, placeholder = "Seleziona orario", dataTestid, intervalMin = 15, className = "" }) {
  const [open, setOpen] = useState(false);
  const gridRef = useRef(null);
  const selectedRef = useRef(null);

  const slots = [];
  for (let m = 0; m < 24 * 60; m += intervalMin) {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    slots.push(`${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
  }

  // Auto-scroll to selected slot when opening
  useEffect(() => {
    if (open && selectedRef.current && gridRef.current) {
      setTimeout(() => {
        selectedRef.current?.scrollIntoView({ block: "center", behavior: "instant" });
      }, 50);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex h-10 w-full items-center justify-between rounded-md border border-[#E8E2D9] bg-white px-3 py-2 text-sm transition hover:border-[#AC5D45]/40 focus:outline-none focus:ring-2 focus:ring-[#AC5D45]/30 ${className}`}
          data-testid={dataTestid}
        >
          <span className="flex items-center gap-2 min-w-0">
            <Clock className="w-4 h-4 text-[#9C9890] shrink-0" />
            <span className={value ? "text-[#1E3A5F] font-medium" : "text-[#9C9890]"}>{value || placeholder}</span>
          </span>
          {open ? <ChevronUp className="w-4 h-4 text-[#9C9890]" /> : <ChevronDown className="w-4 h-4 text-[#9C9890]" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] sm:w-[360px] p-3" align="start">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#635F59] font-semibold mb-2 px-1">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-white border border-[#E8E2D9]" /> ☀️ Diurno · €10</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#E8EEF7]" /> 🌙 Notturno · €20</span>
        </div>
        <div ref={gridRef} className="grid grid-cols-5 gap-1.5 max-h-[300px] overflow-y-auto pr-1">
          {slots.map((t) => {
            const h = parseInt(t.split(":")[0], 10);
            const isNight = h < 8 || h >= 21;
            const isSelected = t === value;
            return (
              <button
                key={t}
                type="button"
                ref={isSelected ? selectedRef : null}
                onClick={() => { onChange(t); setOpen(false); }}
                data-testid={dataTestid ? `${dataTestid}-slot-${t.replace(":", "")}` : undefined}
                className={`text-xs font-semibold rounded-full py-1.5 transition border ${
                  isSelected
                    ? "bg-[#AC5D45] text-white border-[#AC5D45]"
                    : isNight
                    ? "bg-[#E8EEF7] text-[#1E3A5F] border-transparent hover:bg-[#D6E1F0]"
                    : "bg-white text-[#1E3A5F] border-[#E8E2D9] hover:bg-[#FDFBF7]"
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
